import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, CircularProgress, Alert, Avatar, IconButton,
  Tooltip, Card, CardContent, Dialog, DialogTitle,
  DialogContent, DialogActions, Button,
} from "@mui/material";
import {
  Search as SearchIcon, CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon, Refresh as RefreshIcon,
  Person as PersonIcon, FilterList as FilterIcon,
  AttachMoney as MoneyIcon, Receipt as ReceiptIcon,
  Download as DownloadIcon, PictureAsPdf as PdfIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Cell, Legend,
} from "recharts";
import api, { generateUserPDF } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const USER_BASE_URL = process.env.REACT_APP_USER_API_URL || "https://backend.hunarmandpunjab.org.pk";

interface User {
  _id: string; fullName: string; email: string;
  rollNumber: string; mobile: string; courses: string[];
}
interface Challan {
  _id: string; userId: User; challanId: string; amount: number;
  paid: boolean; path?: string; branchCode?: number;
  txnDate?: string; txnId?: number; createdAt: string; updatedAt: string;
}
interface ChallanResponse {
  challans: Challan[];
  pagination: { current: number; pages: number; total: number; limit: number };
}
interface ChallanStats {
  total: number; paid: number; unpaid: number;
  totalRevenue: number; pendingRevenue: number; recent: number;
}

// ── Stat Card ──────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon, color, bg }: any) => (
  <Card sx={{
    flex: "1 1 200px", borderRadius: "12px", border: "none",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden",
    transition: "transform .25s,box-shadow .25s",
    "&:hover": { transform: "translateY(-4px)", boxShadow: `0 12px 28px rgba(0,0,0,0.1)` },
  }}>
    <Box sx={{ height: 3, background: `linear-gradient(90deg,${color},#fbbf24)` }} />
    <CardContent sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>{label}</Typography>
          <Typography sx={{ fontSize: "1.4rem", fontWeight: 500, color: "#1e293b", lineHeight: 1 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 400, mt: 0.5 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

function Challans() {
  const [challans, setChallans] = useState<Challan[]>([]);
  const [stats, setStats] = useState<ChallanStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [paid, setPaid] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const { user } = useAuth();

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedChallanForPDF, setSelectedChallanForPDF] = useState<Challan | null>(null);
  const [adminApiKey, setAdminApiKey] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const isSuperAdmin = user?.role === "superadmin" || user?.role === "superAdmin";
  const isAdmin = user?.role === "admin" || isSuperAdmin;

  useEffect(() => { fetchChallans(); }, [page, rowsPerPage, search, paid, startDate, endDate, minAmount, maxAmount]);
  useEffect(() => { fetchChallanStats(); }, []);

  const fetchChallans = async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit: rowsPerPage, project: "DigiSindh" };
      if (search) params.search = search;
      if (paid !== "") params.paid = paid;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (minAmount) params.minAmount = minAmount;
      if (maxAmount) params.maxAmount = maxAmount;
      const response = await api.get("/challans", { params });
      const data: ChallanResponse = response.data.data;
      setChallans(data.challans);
      setTotal(data.pagination.total);
    } catch (err) { setError("Failed to load challans"); }
    finally { setLoading(false); }
  };

  const fetchChallanStats = async () => {
    try {
      const response = await api.get("/challans/stats", { params: { project: "DigiSindh" } });
      setStats(response.data.data);
    } catch (err) { console.error(err); }
  };

  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setPage(0); };
  const clearFilters = () => { setSearch(""); setPaid(""); setStartDate(""); setEndDate(""); setMinAmount(""); setMaxAmount(""); setPage(0); };

  const downloadChallans = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      if (paid !== "") params.paid = paid;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (minAmount) params.minAmount = minAmount;
      if (maxAmount) params.maxAmount = maxAmount;
      const response = await api.get("/challans/download", { params, responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().split("T")[0];
      link.download = `challans_export_${timestamp}.csv`;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); window.URL.revokeObjectURL(url);
    } catch (err) { setError("Failed to download challans data"); }
    finally { setLoading(false); }
  };

  const formatDate = (d?: string) => !d ? "-" : new Date(d).toLocaleDateString("en-PK");
  const formatCurrency = (amount: number) => new Intl.NumberFormat("en-PK", { style: "currency", currency: "PKR", minimumFractionDigits: 0 }).format(amount);
  const getInitials = (name?: string) => !name ? "" : name.split(" ").map(n => n[0]).join("").toUpperCase();

  const handleGeneratePDF = (challan: Challan) => {
    if (challan.userId && challan.userId._id !== "deleted" && challan.userId._id !== "invalid") {
      setSelectedChallanForPDF(challan); setPdfModalOpen(true);
    } else { alert("Cannot generate PDF: User data is not available"); }
  };

  const confirmGeneratePDF = async () => {
    if (!adminApiKey.trim()) { alert("Please provide the admin API key"); return; }
    if (!selectedChallanForPDF?.userId) { alert("No valid user selected"); return; }
    try {
      setPdfGenerating(true);
      const response = await generateUserPDF(selectedChallanForPDF.userId._id, adminApiKey);
      const data = response.data;
      if (data.status === "success" && data.data?.fileUrl) {
        const uploadPath = data.data.fileUrl.substring(data.data.fileUrl.indexOf("/uploads"));
        window.open(`${USER_BASE_URL}${uploadPath}`, "_blank");
        alert(`Challan opened: ${data.data.challanNumber}`);
      } else { throw new Error("Invalid response format"); }
      setPdfModalOpen(false); setAdminApiKey("");
    } catch (error: any) { alert(`Failed to generate challan: ${error.response?.data?.message || error.message}`); }
    finally { setPdfGenerating(false); }
  };

  const getActiveFilterCount = () => [search, paid !== "" ? paid : "", startDate, endDate, minAmount, maxAmount].filter(Boolean).length;

  const paidCount = stats?.paid || 0;
  const unpaidCount = stats?.unpaid || 0;
  const totalRevenue = stats?.totalRevenue || 0;
  const pendingRevenue = stats?.pendingRevenue || 0;

  const chartData = [
    { name: "Paid", challans: paidCount, color: "#059669" }, // Green
    { name: "Unpaid", challans: unpaidCount, color: "#fbbf24" }, // Gold
  ];
  const revenueData = [
    { name: "Collected", amount: totalRevenue, color: "#059669" },
    { name: "Pending", amount: pendingRevenue, color: "#fbbf24" },
  ];

  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography sx={{ fontSize: "1.4rem", fontWeight: 500, color: "#059669" }}>
            Challans Management
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 400, mt: 0.25 }}>
            {stats ? `${stats.total.toLocaleString()} total challans in database` : "Loading stats..."}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Download CSV">
            <IconButton onClick={downloadChallans} disabled={loading} sx={{ bgcolor: "#f1f5f9", borderRadius: "12px", "&:hover": { bgcolor: "#e2e8f0" } }}>
              <DownloadIcon sx={{ color: "#6366f1" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchChallans} disabled={loading} sx={{ bgcolor: "#f1f5f9", borderRadius: "12px", "&:hover": { bgcolor: "#e2e8f0" } }}>
              <RefreshIcon sx={{ color: "#64748b" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stat Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <StatCard label="Paid Challans" value={paidCount.toLocaleString()} sub={formatCurrency(totalRevenue)} icon={<CheckCircleIcon />} color="#059669" bg="rgba(5,150,105,0.1)" />
        <StatCard label="Unpaid Challans" value={unpaidCount.toLocaleString()} sub={formatCurrency(pendingRevenue) + " pending"} icon={<CancelIcon />} color="#fbbf24" bg="rgba(251,191,36,0.1)" />
        <StatCard label="Total Revenue" value={formatCurrency(totalRevenue)} sub="Collected" icon={<MoneyIcon />} color="#059669" bg="rgba(5,150,105,0.1)" />
        <StatCard label="Pending Revenue" value={formatCurrency(pendingRevenue)} sub="Outstanding" icon={<ReceiptIcon />} color="#fbbf24" bg="rgba(251,191,36,0.1)" />
      </Box>

      {/* Charts Row */}
      <Box display="flex" gap={2.5} mb={3} flexWrap="wrap">
        <Card sx={{ flex: "1 1 300px", borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 500, fontSize: "0.95rem", color: "#059669", mb: 2 }}>Challan Status Overview</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b", fontWeight: 500 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <RTooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", fontSize: "0.82rem" }} />
                <Bar dataKey="challans" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card sx={{ flex: "1 1 300px", borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 500, fontSize: "0.95rem", color: "#059669", mb: 2 }}>Revenue Breakdown (PKR)</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revenueData} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b", fontWeight: 500 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <RTooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", fontSize: "0.82rem" }} formatter={(v: any) => formatCurrency(v)} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                  {revenueData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <FilterIcon sx={{ color: "#6366f1", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b" }}>Filters</Typography>
              {getActiveFilterCount() > 0 && (
                <Chip label={`${getActiveFilterCount()} active`} size="small" sx={{ bgcolor: "#6366f1", color: "white", fontWeight: 700, fontSize: "0.7rem" }} />
              )}
            </Box>
            {getActiveFilterCount() > 0 && (
              <Button size="small" onClick={clearFilters} sx={{ color: "#ef4444", fontWeight: 600, fontSize: "0.78rem", textTransform: "none" }}>
                Clear All
              </Button>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <TextField size="small" label="Search" placeholder="Challan ID, Transaction ID" value={search} onChange={handleSearchChange}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "#94a3b8", fontSize: 18 }} /> }}
              sx={{ minWidth: 230, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <FormControl size="small" sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}>
              <InputLabel>Payment Status</InputLabel>
              <Select value={paid} label="Payment Status" onChange={(e) => setPaid(e.target.value as string)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Paid</MenuItem>
                <MenuItem value="false">Unpaid</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <TextField size="small" label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <TextField size="small" label="Min Amount" type="number" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            <TextField size="small" label="Max Amount" type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                {["User", "Roll & Mobile", "Courses", "Challan ID", "Amount", "Status", "Transaction", "Created", "Paid On", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 500, fontSize: "0.75rem", color: "#059669", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #e2e8f0", py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><CircularProgress size={36} sx={{ color: "#6366f1" }} /></TableCell></TableRow>
              ) : challans.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: "#94a3b8", fontWeight: 500 }}>No challans found</TableCell></TableRow>
              ) : (
                challans.map((challan) => (
                  <TableRow key={challan._id} hover sx={{ "&:hover": { bgcolor: "#f8fafc" }, transition: "background .15s" }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: "#6366f1", width: 36, height: 36, fontSize: "0.8rem", fontWeight: 700 }}>
                          {challan.userId?.fullName ? getInitials(challan.userId.fullName) : <PersonIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#1e293b" }}>{challan.userId?.fullName || "Unknown"}</Typography>
                          <Typography sx={{ fontSize: "0.73rem", color: "#94a3b8" }}>{challan.userId?.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.8rem", color: "#334155" }}>R: {challan.userId?.rollNumber}</Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>M: {challan.userId?.mobile}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {challan.userId?.courses?.length > 0
                          ? challan.userId.courses.map((c, i) => <Chip key={i} label={c} size="small" sx={{ fontSize: "0.68rem", height: 20 }} />)
                          : <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>-</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#334155" }}>{challan.challanId}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#059669" }}>{formatCurrency(challan.amount)}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        icon={challan.paid ? <CheckCircleIcon /> : <CancelIcon />}
                        label={challan.paid ? "Paid" : "Unpaid"}
                        size="small"
                        sx={{
                          fontWeight: 500, fontSize: "0.72rem",
                          bgcolor: challan.paid ? "rgba(5,150,105,0.1)" : "rgba(251,191,36,0.1)",
                          color: challan.paid ? "#059669" : "#d97706",
                          border: "none",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {challan.paid ? (
                        <Box>
                          {challan.txnId && <Typography sx={{ fontSize: "0.78rem", color: "#334155" }}>Txn: {challan.txnId}</Typography>}
                          {challan.branchCode && <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Branch: {challan.branchCode}</Typography>}
                        </Box>
                      ) : <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>-</Typography>}
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{formatDate(challan.createdAt)}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{challan.paid ? formatDate(challan.txnDate) : "-"}</Typography></TableCell>
                    <TableCell>
                      <Tooltip title="Generate Challan PDF">
                        <IconButton size="small" onClick={() => handleGeneratePDF(challan)}
                          disabled={!challan.userId || challan.userId._id === "deleted" || challan.userId._id === "invalid"}
                          sx={{ bgcolor: "rgba(99,102,241,0.08)", borderRadius: "8px", "&:hover": { bgcolor: "rgba(99,102,241,0.15)" } }}>
                          <PdfIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={total} rowsPerPage={rowsPerPage} page={page} onPageChange={handlePageChange} onRowsPerPageChange={handleRowsPerPageChange} />
      </Card>

      {/* PDF Dialog */}
      <Dialog open={pdfModalOpen} onClose={() => setPdfModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" } }}>
        <DialogTitle sx={{ fontWeight: 500, color: "#059669" }}>
          <Box display="flex" alignItems="center" gap={1}><PdfIcon sx={{ color: "#fbbf24" }} /> Generate Challan PDF</Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>
            This will generate or open the challan PDF for the selected user in a new tab.
          </Alert>
          {selectedChallanForPDF?.userId && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: "12px" }}>
              <strong>User:</strong> {selectedChallanForPDF.userId.fullName}<br />
              <strong>Roll:</strong> {selectedChallanForPDF.userId.rollNumber}<br />
              <strong>Challan ID:</strong> {selectedChallanForPDF.challanId}
            </Alert>
          )}
          <TextField label="Admin API Key (Required)" value={adminApiKey} onChange={(e) => setAdminApiKey(e.target.value)} fullWidth type="password" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} helperText="Required to authenticate PDF generation" />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setPdfModalOpen(false)} sx={{ borderRadius: "10px" }}>Cancel</Button>
          <Button onClick={confirmGeneratePDF} variant="contained" disabled={!adminApiKey.trim() || pdfGenerating}
            startIcon={pdfGenerating ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />}
            sx={{ borderRadius: "10px", bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", fontWeight: 700 }}>
            {pdfGenerating ? "Processing..." : "Generate & Open"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Challans;
